import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { SINGLE_SERIES_COLOR, TOKEN_SERIES, dayLabel, formatCost, formatTokens } from "../../lib/assistantUsage";

// The Copilote's usage over the period: one measure per chart (never two y-scales), thin bars with a 2 px gap, a hover
// tooltip, recessive axes, and the same figures as a table for whoever cannot read the colours.

const AXIS = { fontSize: 11, fill: "#64748b" };

function ChartTooltip({ active, payload, label, format }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-md">
      <div className="mb-1 font-medium text-slate-900">{dayLabel(label)}</div>
      {payload.slice().reverse().map((entry) => (
        <div key={entry.dataKey} className="flex items-center gap-2 text-slate-600">
          <span className="h-2 w-2 rounded-sm" style={{ background: entry.color }} aria-hidden="true" />
          <span>{entry.name}</span>
          <span className="ml-auto pl-3 font-medium tabular-nums text-slate-900">{format(entry.value)}</span>
        </div>
      ))}
    </div>
  );
}

function ChartFrame({ title, children, legend }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-slate-900">{title}</h4>
        {legend}
      </div>
      <div className="h-44">{children}</div>
    </div>
  );
}

function SingleBars({ days, dataKey, name, format, testId }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={days} margin={{ top: 4, right: 4, bottom: 0, left: 0 }} barCategoryGap={2} data-testid={testId}>
        <CartesianGrid vertical={false} stroke="#eef2f6" />
        <XAxis dataKey="day" tickFormatter={dayLabel} tick={AXIS} axisLine={false} tickLine={false} minTickGap={16} />
        <YAxis tick={AXIS} axisLine={false} tickLine={false} width={68} tickFormatter={format} allowDecimals={false} />
        <Tooltip cursor={{ fill: "#f1f5f9" }} content={<ChartTooltip format={format} />} />
        <Bar dataKey={dataKey} name={name} fill={SINGLE_SERIES_COLOR} radius={[4, 4, 0, 0]} maxBarSize={28} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function UsageCharts({ days }) {
  const tokens = (value) => formatTokens(value, { short: true });
  return (
    <div className="space-y-4" data-testid="usage-charts">
      <div className="grid gap-4 lg:grid-cols-2">
        <ChartFrame title="Coût estimé par jour">
          <SingleBars days={days} dataKey="cost_nanodollars" name="Coût estimé" format={formatCost} testId="chart-cost" />
        </ChartFrame>
        <ChartFrame title="Questions par jour">
          <SingleBars days={days} dataKey="questions" name="Questions" format={(v) => formatTokens(v)} testId="chart-questions" />
        </ChartFrame>
      </div>
      <ChartFrame
        title="Tokens par jour"
        legend={(
          <ul className="flex flex-wrap gap-3 text-xs text-slate-600" aria-label="Légende">
            {TOKEN_SERIES.map((s) => (
              <li key={s.key} className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-sm" style={{ background: s.color }} aria-hidden="true" />{s.label}
              </li>
            ))}
          </ul>
        )}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={days} margin={{ top: 4, right: 4, bottom: 0, left: 0 }} barCategoryGap={2}>
            <CartesianGrid vertical={false} stroke="#eef2f6" />
            <XAxis dataKey="day" tickFormatter={dayLabel} tick={AXIS} axisLine={false} tickLine={false} minTickGap={16} />
            <YAxis tick={AXIS} axisLine={false} tickLine={false} width={56} tickFormatter={tokens} allowDecimals={false} />
            <Tooltip cursor={{ fill: "#f1f5f9" }} content={<ChartTooltip format={(v) => formatTokens(v)} />} />
            {TOKEN_SERIES.map((s, i) => (
              <Bar key={s.key} dataKey={s.key} name={s.label} stackId="tokens" fill={s.color} stroke="#ffffff" strokeWidth={1}
                radius={i === TOKEN_SERIES.length - 1 ? [4, 4, 0, 0] : 0} maxBarSize={28} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </ChartFrame>
      <details className="rounded-xl border border-slate-200 bg-white text-sm">
        <summary className="cursor-pointer select-none px-4 py-3 font-medium text-slate-700">Voir les chiffres par jour</summary>
        <div className="overflow-x-auto px-4 pb-4">
          <table className="w-full min-w-[560px] text-xs" data-testid="usage-days-table">
            <thead>
              <tr className="text-left text-slate-500">
                <th className="py-2 pr-3 font-semibold">Jour</th>
                <th className="py-2 pr-3 text-right font-semibold">Questions</th>
                <th className="py-2 pr-3 text-right font-semibold">Coût estimé</th>
                {TOKEN_SERIES.map((s) => <th key={s.key} className="py-2 pr-3 text-right font-semibold">{s.label}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 tabular-nums">
              {days.filter((d) => d.questions).map((d) => (
                <tr key={d.day}>
                  <td className="py-1.5 pr-3">{dayLabel(d.day)}</td>
                  <td className="py-1.5 pr-3 text-right">{d.questions}</td>
                  <td className="py-1.5 pr-3 text-right">{formatCost(d.cost_nanodollars)}</td>
                  {TOKEN_SERIES.map((s) => <td key={s.key} className="py-1.5 pr-3 text-right">{formatTokens(d[s.key])}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}

export function TopTools({ tools }) {
  if (!tools.length) return null;
  const max = Math.max(...tools.map((t) => t.calls));
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="usage-tools">
      <h4 className="mb-3 text-sm font-semibold text-slate-900">Outils les plus utilisés</h4>
      <ul className="space-y-2.5">
        {tools.slice(0, 10).map((t) => (
          <li key={t.tool} className="text-sm">
            <div className="flex items-baseline justify-between gap-3">
              <span className="truncate text-slate-700">{t.label}</span>
              <span className="shrink-0 text-xs tabular-nums text-slate-500">
                {t.calls} appel{t.calls > 1 ? "s" : ""}{t.errors ? ` · ${t.errors} en échec` : ""}
              </span>
            </div>
            <div className="mt-1 h-1.5 rounded-full bg-slate-100">
              <div className="h-1.5 rounded-full bg-jamora" style={{ width: `${Math.max(4, (t.calls / max) * 100)}%` }} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
